import { Button, TextField, Divider } from '@material-ui/core';
import CheckOutlined from '@material-ui/icons/CheckOutlined';
import Error from '@material-ui/icons/Error';
import FileCopyOutlined from '@material-ui/icons/FileCopyOutlined';
import { useQuery } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import React, { useRef, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useLocation } from 'react-router-dom';
import { SUBSCRIPTIONS_API_PUBLIC_URL } from 'settings';
import GlobalLoading from 'v2/components/Shared/GlobalLoading';
import UnloggedLayout from 'v2/components/Shared/UnloggedLayout';
import { StoreCommunicationMessageLogActionSlug, StoreCommunicationMessageLogOrigin } from 'v2/interfaces/subscriptions';
import subscriptionsService from 'v2/services/subscriptions';

import { useStyles } from './styles';

type Response = {
  pix: {
    url: string;
    qrCodeBase64: string;
  };
  recoveryUrl: string;
};

function Content() {
  const { search } = useLocation();
  const sentMessageId = new URLSearchParams(search).get('sentMessageId');
  const { data, isLoading, isError, error } = useQuery<Response>({
    queryKey: ['validateWhatsAppMessage', sentMessageId],
    queryFn: async () => {
      const response = await axios.post(`${SUBSCRIPTIONS_API_PUBLIC_URL}/public/v1/validate-whatsapp-sent-message`, {
        sentMessageId,
      });
      return response.data;
    },
    enabled: !!sentMessageId,
    retry: false,
  });
  const classes = useStyles();
  const [didCopy, setDidCopy] = useState(false);
  const shouldSaveCopy = useRef(true);

  async function onCopy() {
    setDidCopy(true);
    setTimeout(() => {
      setDidCopy(false);
    }, 3000);

    if (shouldSaveCopy.current) {
      try {
        await subscriptionsService
          .storeCommunicationMessageLog({
            id: sentMessageId,
            actionSlug: StoreCommunicationMessageLogActionSlug.COPIED,
            origin: StoreCommunicationMessageLogOrigin.REDIRECT_PAGE,
            context: 'Pix copia e cola copiado',
            createdAt: new Date().toISOString(),
          })
          .toPromise();
        shouldSaveCopy.current = false;
      } catch (err) {
        console.warn('Error while saving pix copy log');
      }
    }
  }

  if (isLoading) {
    return <GlobalLoading />;
  }

  if (isError || !data) {
    console.error(error);
    const isAxiosError = error instanceof AxiosError;
    const defaultMessage = 'Erro ao carregar dados para pagamento.';

    return (
      <div className={classes.errorContainer}>
        <Error className={classes.errorIcon} />
        {isAxiosError ? (
          <h3>{error.response.data?.message ?? defaultMessage}</h3>
        ) : (
          <>
            <h3>Erro ao carregar os dados.</h3>
            <p>Por favor, tente novamente mais tarde.</p>
          </>
        )}
      </div>
    );
  }

  const {
    pix: { url, qrCodeBase64 },
    recoveryUrl,
  } = data;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h3>Pagar com Pix Copia e Cola</h3>
        <p>Se preferir, copie o código Pix abaixo, no seu banco selecione a opção Pix Copia e Cola e finalize sua compra!</p>
        <TextField label='Código de pagamento' type='text' value={url} fullWidth disabled />
        <CopyToClipboard text={url} onCopy={onCopy}>
          <Button
            className={classes.copyButton}
            startIcon={didCopy ? <CheckOutlined /> : <FileCopyOutlined />}
            type='button'
            variant='text'
            color='primary'
          >
            {didCopy ? 'Código copiado!' : 'Copiar código Pix'}
          </Button>
        </CopyToClipboard>
      </div>
      <Divider className={classes.divider} />
      <div className={classes.body}>
        <div className={classes.bodyTextContainer}>
          <h3>Pagar com QR Code</h3>
          <ol className={classes.orderedList}>
            <li>
              <span>1.</span> Acesse sua conta bancária no Internet Banking ou através do app;
            </li>
            <li>
              <span>2.</span> Selecione a opção de pagar via Pix;
            </li>
            <li>
              <span>3.</span> Escaneie o código Pix ao lado e finalize o pagamento.
            </li>
          </ol>
        </div>
        <div className={classes.qrCodeContainer}>
          <img src={qrCodeBase64} alt='QR Code' />
        </div>
      </div>
      <Divider className={classes.divider} />
      <div className={classes.footer}>
        <p>
          Deseja utilizar outras formas de pagamento? <a href={recoveryUrl}>Acesse aqui.</a>
        </p>
        <div className={classes.links}>
          <a href='https://www.eduzz.com/privacidade' target='_blank' rel='noopener noreferrer'>
            Política de privacidade
          </a>
          <a href='https://www.eduzz.com/termos' target='_blank' rel='noopener noreferrer'>
            Termos de compra
          </a>
        </div>
      </div>
    </div>
  );
}

export function SubscriptionLateInvoicePayment() {
  return (
    <UnloggedLayout>
      <Content />
    </UnloggedLayout>
  );
}
